import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchLatestCrimeDate, fetchCrimeCasesByDate, submitAttemptAndUpdateLeaderboard, fetchUserAttempts, fetchAllCrimeDates } from '../../services/crimeGameService';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';
import { translateText } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { CrimeCase, Suspect, Tip } from '../../types/crime-game';
import { SuspectGrid } from './SuspectGrid';
import { CluePlayer } from './CluePlayer';
import { QuestionModal } from './QuestionModal';
import { GuessModal } from './GuessModal';
import { ResultPanel } from './ResultPanel';
import { LeaderboardPanel } from './LeaderboardPanel';
import { ConfirmationModal } from './ConfirmationModal';
import { AnsweredQuestionsLog } from './AnsweredQuestionsLog';
import { ClickableText } from './ClickableText';
import { Loader2, AlertCircle, Search, Siren, BookOpen, Languages, Trophy, Info, Flame, Frown, Volume2, VolumeX } from 'lucide-react';
import { fetchCurrentUserLeaderboardEntry, fetchCrimeWeekActivity } from '../../services/crimeGameService';
import { GameLeaderboardEntry } from '../../types/crime-game';
import { FeatureSummary } from '../gamification/FeatureSummary';

const formatIntoParagraphs = (text: string): string[] => {
  if (!text) return [];
  if (text.includes('\n\n')) {
    return text.split(/\n\n+/).filter(p => p.trim().length > 0);
  }
  if (text.includes('\n')) {
    return text.split(/\n+/).filter(p => p.trim().length > 0);
  }
  
  const sentences = text.match(/[^.!?]+[.!?]+["']?\s*/g);
  if (!sentences || sentences.length === 0) return [text];
  
  const paragraphs: string[] = [];
  let current = "";
  
  for (let i = 0; i < sentences.length; i++) {
    current += sentences[i];
    if ((i + 1) % 3 === 0 || i === sentences.length - 1) {
      paragraphs.push(current.trim());
      current = "";
    }
  }
  return paragraphs;
};

export default function CrimeGameContainer() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { speak, stop, isSpeaking, isSupported } = useSpeechSynthesis({ lang: 'en-US' }); // Default to English for clues, maybe configurable

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoSpeak, setAutoSpeak] = useState(false); // Default off

  // Data State
  const [dateKey, setDateKey] = useState<string | null>(null);
  const [originalDateKey, setOriginalDateKey] = useState<string | null>(null);
  const [cases, setCases] = useState<CrimeCase[]>([]);
  const [originalCases, setOriginalCases] = useState<CrimeCase[]>([]);
  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
  const currentCase = cases[currentCaseIndex];
  const [pastDates, setPastDates] = useState<string[]>([]);
  const [casesByDate, setCasesByDate] = useState<Map<string, CrimeCase[]>>(new Map());
  const [selectedPastDate, setSelectedPastDate] = useState<string | null>(null);
  const [loadingPastCases, setLoadingPastCases] = useState(false);
  const [playedDates, setPlayedDates] = useState<Set<string>>(new Set());
  const [solvedDates, setSolvedDates] = useState<Set<string>>(new Set());
  const [visiblePastDates, setVisiblePastDates] = useState<number>(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // User State
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isSolved, setIsSolved] = useState(false);
  const [userStats, setUserStats] = useState<GameLeaderboardEntry | null>(null);

  // Game Play State
  const [gameState, setGameState] = useState<'landing' | 'selection' | 'investigation' | 'result' | 'summary'>('landing');
  const [showSummary, setShowSummary] = useState(false);
  const [previousStreak, setPreviousStreak] = useState(0);
  const [crimeWeekHistory, setCrimeWeekHistory] = useState<boolean[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [revealedTipsCount, setRevealedTipsCount] = useState(0);
  const [revealedNextTipFor, setRevealedNextTipFor] = useState<Set<string>>(new Set());
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  const [correctlyAnsweredQuestions, setCorrectlyAnsweredQuestions] = useState<Set<string>>(new Set());
  const [wronglyAnsweredQuestions, setWronglyAnsweredQuestions] = useState<Set<string>>(new Set());
  const [skippedQuestions, setSkippedQuestions] = useState<Set<string>>(new Set());
  const [correctQuestions, setCorrectQuestions] = useState(0);
  const [revealedAnswers, setRevealedAnswers] = useState<Set<string>>(new Set());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [selectedSuspect, setSelectedSuspect] = useState<Suspect | null>(null);
  const [isGuessModalOpen, setIsGuessModalOpen] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  
  // Translation tracking
  const [introTranslated, setIntroTranslated] = useState(false);
  const [introTranslationText, setIntroTranslationText] = useState<string | null>(null);
  const [questionTranslations, setQuestionTranslations] = useState<Map<string, string>>(new Map());
  const [tipTranslations, setTipTranslations] = useState<Map<string, string>>(new Map());
  const [translationCounts, setTranslationCounts] = useState({
    intro: 0,
    questions: 0,
    tips: 0,
    words: 0
  });
  
  const [startTime, setStartTime] = useState<number>(0);

  // Result State
  const [lastResult, setLastResult] = useState<{
    isWin: boolean;
    score: number;
    breakdown: any[];
  } | null>(null);

  // Confirmation modals
  const [showTranslationConfirm, setShowTranslationConfirm] = useState(false);
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);
  const [showWordTranslationConfirm, setShowWordTranslationConfirm] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [pendingTranslationAction, setPendingTranslationAction] = useState<(() => void) | null>(null);
  const [pendingRevealAction, setPendingRevealAction] = useState<(() => void) | null>(null);
  const [pendingWordTranslation, setPendingWordTranslation] = useState<string | null>(null);
  const [confirmedWordTranslation, setConfirmedWordTranslation] = useState<string | null>(null);
  const [pendingSkipAction, setPendingSkipAction] = useState<(() => void) | null>(null);
  const [translationType, setTranslationType] = useState<'intro' | 'question' | 'tip' | 'word' | null>(null);

  // Load Game State from localStorage
  const loadGameState = useCallback((caseToPlay: CrimeCase, dKey: string, uid: string) => {
    const storageKey = `crime-game-progress-${uid}-${dKey}-${caseToPlay.id}`;
    const savedStateStr = localStorage.getItem(storageKey);
    if (savedStateStr) {
      try {
        const savedState = JSON.parse(savedStateStr);
        setRevealedTipsCount(savedState.revealedTipsCount || 0);
        setRevealedNextTipFor(new Set(savedState.revealedNextTipFor || []));
        setAnsweredQuestions(new Set(savedState.answeredQuestions || []));
        setCorrectlyAnsweredQuestions(new Set(savedState.correctlyAnsweredQuestions || []));
        setWronglyAnsweredQuestions(new Set(savedState.wronglyAnsweredQuestions || []));
        setSkippedQuestions(new Set(savedState.skippedQuestions || []));
        setCorrectQuestions(savedState.correctQuestions || 0);
        setRevealedAnswers(new Set(savedState.revealedAnswers || []));
        setIntroTranslated(savedState.introTranslated || false);
        setIntroTranslationText(savedState.introTranslationText || null);
        setQuestionTranslations(new Map(savedState.questionTranslations || []));
        setTipTranslations(new Map(savedState.tipTranslations || []));
        setTranslationCounts(savedState.translationCounts || { intro: 0, questions: 0, tips: 0, words: 0 });
        setStartTime(savedState.startTime || Date.now());
        setHintsUsed(savedState.hintsUsed || 0);
        return;
      } catch (e) {
        console.error("Failed to parse saved state", e);
      }
    }
    // Default
    setTranslationCounts({ questions: 0, tips: 0, intro: 0, words: 0 });
    setRevealedTipsCount(0);
    setRevealedNextTipFor(new Set());
    setAnsweredQuestions(new Set());
    setCorrectlyAnsweredQuestions(new Set());
    setWronglyAnsweredQuestions(new Set());
    setSkippedQuestions(new Set());
    setRevealedAnswers(new Set());
    setCorrectQuestions(0);
    setIntroTranslated(false);
    setIntroTranslationText(null);
    setQuestionTranslations(new Map());
    setTipTranslations(new Map());
    setStartTime(Date.now());
    setHintsUsed(0);
  }, []);

  // Sync back button / URL state with game state
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'playing' && gameState === 'landing') {
      if (currentCase && dateKey && currentUser) {
        loadGameState(currentCase, dateKey, currentUser.uid);
      }
      setGameState('investigation');
    } else if (status !== 'playing' && gameState === 'investigation') {
      setGameState('landing');
    }
  }, [searchParams, gameState, currentCase, dateKey, currentUser, loadGameState]);

  // Save game state when it changes
  useEffect(() => {
    if (gameState === 'investigation' && currentCase && dateKey && currentUser) {
      const timeoutId = setTimeout(() => {
        const storageKey = `crime-game-progress-${currentUser.uid}-${dateKey}-${currentCase.id}`;
        const stateToSave = {
          revealedTipsCount,
          revealedNextTipFor: Array.from(revealedNextTipFor),
          answeredQuestions: Array.from(answeredQuestions),
          correctlyAnsweredQuestions: Array.from(correctlyAnsweredQuestions),
          wronglyAnsweredQuestions: Array.from(wronglyAnsweredQuestions),
          skippedQuestions: Array.from(skippedQuestions),
          correctQuestions,
          revealedAnswers: Array.from(revealedAnswers),
          introTranslated,
          introTranslationText,
          questionTranslations: Array.from(questionTranslations.entries()),
          tipTranslations: Array.from(tipTranslations.entries()),
          translationCounts,
          startTime,
          hintsUsed,
        };
        localStorage.setItem(storageKey, JSON.stringify(stateToSave));
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [
    gameState, currentCase, dateKey, currentUser,
    revealedTipsCount, revealedNextTipFor, answeredQuestions, correctlyAnsweredQuestions, wronglyAnsweredQuestions, skippedQuestions,
    correctQuestions, revealedAnswers, introTranslated, introTranslationText,
    questionTranslations, tipTranslations, translationCounts, startTime, hintsUsed
  ]);

  // Track if we've already loaded past cases to prevent infinite loops
  const hasLoadedPastCases = useRef<string>('');

  const loadCasesForDates = async (datesToLoad: string[]) => {
    const datesToFetch = datesToLoad.filter(d => !casesByDate.has(d));
    if (datesToFetch.length === 0) return;

    const fetchPromises = datesToFetch.map(async (date) => {
      const dateCases = await fetchCrimeCasesByDate(date);
      let userPlayed = false;
      let userSolved = false;

      if (currentUser) {
        const attempts = await fetchUserAttempts(date, currentUser.uid);
        if (attempts.length > 0) {
          userPlayed = true;
          userSolved = attempts.some(a => a.result === 'win');
        }
      }
      return { date, dateCases, userPlayed, userSolved };
    });

    const results = await Promise.all(fetchPromises);

    setCasesByDate(prev => {
      const newMap = new Map(prev);
      for (const { date, dateCases } of results) {
        if (dateCases.length > 0) {
          newMap.set(date, dateCases);
        }
      }
      return newMap;
    });

    setPlayedDates(prev => {
      const newSet = new Set(prev);
      for (const { date, userPlayed } of results) {
        if (userPlayed) newSet.add(date);
      }
      return newSet;
    });

    setSolvedDates(prev => {
      const newSet = new Set(prev);
      for (const { date, userSolved } of results) {
        if (userSolved) newSet.add(date);
      }
      return newSet;
    });
  };

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    const nextCount = visiblePastDates + 10;
    const datesToLoad = pastDates.slice(visiblePastDates, nextCount);
    await loadCasesForDates(datesToLoad);
    setVisiblePastDates(nextCount);
    setIsLoadingMore(false);
  };

  // Load past dates and cases when entering selection screen
  // Only load once per originalDateKey and user when entering selection state
  useEffect(() => {
    if (gameState === 'selection' && originalDateKey) {
      const loadKey = `${originalDateKey}-${currentUser?.uid || 'guest'}`;
      // Only load if we haven't loaded for this dateKey yet
      if (hasLoadedPastCases.current === loadKey) return;

      hasLoadedPastCases.current = loadKey;
      setLoadingPastCases(true);

      (async () => {
        try {
          const dates = await fetchAllCrimeDates(originalDateKey);
          setPastDates(dates);
          await loadCasesForDates(dates.slice(0, 10));
        } catch (error) {
          console.error('Error loading past cases:', error);
          hasLoadedPastCases.current = ''; // Reset on error so we can retry
        } finally {
          setLoadingPastCases(false);
        }
      })();
    }
  }, [gameState, originalDateKey, currentUser]);

  useEffect(() => {
    initGame();
  }, []);

  const initGame = async () => {
    setLoading(true);
    setError(null);
    try {
      const latestDate = await fetchLatestCrimeDate();
      if (!latestDate) {
        setError('No cases available yet.');
        setLoading(false);
        return;
      }
      setDateKey(latestDate);

      const fetchedCases = await fetchCrimeCasesByDate(latestDate);
      if (fetchedCases.length === 0) {
        setError('No cases found for the latest date.');
      } else {
        setCases(fetchedCases);
        setOriginalCases(fetchedCases);
        setOriginalDateKey(latestDate);
        // Default to landing page showing the first case as "Crime of the Day"
        // We set currentCaseIndex to 0 for the featured case
        setCurrentCaseIndex(0);
        setGameState('landing');

        // Check user attempts for the original date
        if (currentUser) {
          const attempts = await fetchUserAttempts(latestDate, currentUser.uid);
          if (attempts.length > 0) {
            setHasPlayed(true);
            const won = attempts.some(a => a.result === 'win');
            setIsSolved(won);
          } else {
            setHasPlayed(false);
            setIsSolved(false);
          }

          // Fetch streak stats
          try {
            const stats = await fetchCurrentUserLeaderboardEntry(currentUser.uid, 'global');
            setUserStats(stats);
            setPreviousStreak(stats?.dailyStreak || 0);
            
            // Load week history
            const weekHistory = await fetchCrimeWeekActivity(currentUser.uid);
            setCrimeWeekHistory(weekHistory);
          } catch (e) {
            console.error("Failed to fetch user stats", e);
          }
        }
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load game data.');
    } finally {
      setLoading(false);
    }
  };

  const selectCase = (index: number) => {
    const caseToPlay = cases[index];
    if (caseToPlay && dateKey && currentUser) {
      loadGameState(caseToPlay, dateKey, currentUser.uid);
    } else {
      setTranslationCounts({ questions: 0, tips: 0, intro: 0, words: 0 });
      setRevealedTipsCount(0);
      setRevealedNextTipFor(new Set());
      setAnsweredQuestions(new Set());
      setCorrectlyAnsweredQuestions(new Set());
      setWronglyAnsweredQuestions(new Set());
      setSkippedQuestions(new Set());
      setRevealedAnswers(new Set());
      setCorrectQuestions(0);
      setIntroTranslated(false);
      setIntroTranslationText(null);
      setQuestionTranslations(new Map());
      setTipTranslations(new Map());
      setStartTime(Date.now());
      setHintsUsed(0);
    }
    
    setCurrentQuestionIndex(0);
    setSelectedSuspect(null);
    setLastResult(null);

    setSearchParams({ status: 'playing' });
    setGameState('investigation');
    setIsGuessModalOpen(false);
  };

  // Filter suspects to only show actual suspects (c2, c3, c4)
  const actualSuspects = useMemo(() => {
    if (!currentCase) return [];
    const suspectIds = currentCase.suspectIds.length > 0 
      ? currentCase.suspectIds 
      : ['c2', 'c3', 'c4']; // Fallback
    return currentCase.suspects.filter(s => suspectIds.includes(s.id));
  }, [currentCase]);

  // Create suspect name map for CluePlayer
  const suspectNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (currentCase) {
      currentCase.suspects.forEach(s => map.set(s.id, s.name));
    }
    return map;
  }, [currentCase]);

  // Get unlocked tips based ONLY on explicit reveals
  const unlockedTips = useMemo(() => {
    if (!currentCase) return [];
    
    const unlocked = currentCase.tips.filter(tip => revealedNextTipFor.has(tip.questionId));
    
    // Maintain original order
    return unlocked.sort((a, b) => {
      const aIndex = currentCase.questions.findIndex(q => q.id === a.questionId);
      const bIndex = currentCase.questions.findIndex(q => q.id === b.questionId);
      return aIndex - bIndex;
    });
  }, [currentCase, revealedNextTipFor]);

  const handleQuestionAnswer = (isCorrect: boolean, revealedAnswer: boolean) => {
    if (!currentCase) return;

    const currentQuestion = currentCase.questions[currentQuestionIndex];
    if (!currentQuestion) return;

    // Mark question as answered
    const newAnsweredQuestions = new Set(answeredQuestions);
    newAnsweredQuestions.add(currentQuestion.id);
    setAnsweredQuestions(newAnsweredQuestions);

    if (revealedAnswer) {
      setRevealedAnswers(prev => new Set(prev).add(currentQuestion.id));
    } else if (isCorrect) {
      setCorrectQuestions(prev => prev + 1);
      setCorrectlyAnsweredQuestions(prev => new Set(prev).add(currentQuestion.id));
    } else {
      setWronglyAnsweredQuestions(prev => new Set(prev).add(currentQuestion.id));
    }

    // Close modal first
    setIsQuestionModalOpen(false);

    // Note: We no longer auto-reveal tips when a question is answered!
  };

  const handleSkipQuestion = () => {
    if (!currentCase) return;

    const currentQuestion = currentCase.questions[currentQuestionIndex];
    if (!currentQuestion) return;

    // Check if first skip of the day - show confirmation
    if (isFirstSkipOfDay()) {
      setPendingSkipAction(() => () => {
        performSkip();
        markFirstSkipUsed();
      });
      setShowSkipConfirm(true);
      return;
    }

    // Not first skip, proceed directly
    performSkip();
  };

  const performSkip = () => {
    if (!currentCase) return;

    const currentQuestion = currentCase.questions[currentQuestionIndex];
    if (!currentQuestion) return;

    // Mark question as answered (but incorrect - no points)
    const newAnsweredQuestions = new Set(answeredQuestions);
    newAnsweredQuestions.add(currentQuestion.id);
    setAnsweredQuestions(newAnsweredQuestions);
    
    // Mark current question as skipped so its tip won't be shown
    const newSkippedQuestions = new Set(skippedQuestions);
    newSkippedQuestions.add(currentQuestion.id);
    
    // Find the NEXT question
    const nextQuestionIndex = currentCase.questions.findIndex((q, idx) => 
      idx > currentQuestionIndex && !newAnsweredQuestions.has(q.id)
    );
    
    setSkippedQuestions(newSkippedQuestions);

    // Close modal
    setIsQuestionModalOpen(false);
    
    // Automatically open the next question if available
    if (nextQuestionIndex >= 0) {
      setCurrentQuestionIndex(nextQuestionIndex);
      setIsQuestionModalOpen(true);
    }
  };

  const handleRevealNextTip = () => {
    if (!currentCase) return;
    
    // Find the first question whose tip hasn't been revealed yet
    const unrevealedQuestion = currentCase.questions.find((q) => 
      !revealedNextTipFor.has(q.id)
    );
    
    if (unrevealedQuestion) {
      setRevealedNextTipFor(prev => new Set(prev).add(unrevealedQuestion.id));
      setRevealedTipsCount(prev => prev + 1);
      
      // Auto-narrate tip if enabled
      if (autoSpeak) {
        const nextTip = currentCase.tips.find(t => t.questionId === unrevealedQuestion.id);
        if (nextTip) {
          const lang = nextTip.voice ? nextTip.voice.split('-').slice(0, 2).join('-') : 'en-US';
          speak(nextTip.text, lang);
          setPlayingId(nextTip.id);
        }
      }
    }
  };

  // Check if this is the first translation ever
  const isFirstTranslationOfDay = () => {
    const key = `crime-game-first-translation-ever`;
    return localStorage.getItem(key) !== 'true';
  };

  // Check if this is the first word translation ever
  const isFirstWordTranslationOfDay = () => {
    const key = `crime-game-first-word-translation-ever`;
    return localStorage.getItem(key) !== 'true';
  };

  // Check if this is the first skip ever
  const isFirstSkipOfDay = () => {
    const key = `crime-game-first-skip-ever`;
    return localStorage.getItem(key) !== 'true';
  };

  // Mark first skip as used
  const markFirstSkipUsed = () => {
    const key = `crime-game-first-skip-ever`;
    localStorage.setItem(key, 'true');
  };

  // Mark first translation as used
  const markFirstTranslationUsed = () => {
    const key = `crime-game-first-translation-ever`;
    localStorage.setItem(key, 'true');
  };

  // Mark first word translation as used
  const markFirstWordTranslationUsed = () => {
    const key = `crime-game-first-word-translation-ever`;
    localStorage.setItem(key, 'true');
  };

  // Handle word click/translation
  const handleWordClick = (word: string): boolean => {
    if (!currentCase) return false;
    
    // Check if first word translation of the day
    if (isFirstWordTranslationOfDay()) {
      setTranslationType('word');
      setPendingWordTranslation(word);
      setShowWordTranslationConfirm(true);
      return false; // Don't show popover yet, wait for confirmation
    }

    // Not first word translation, proceed normally (just count it)
    setTranslationCounts(prev => ({ ...prev, words: prev.words + 1 }));
    return true; // Show popover immediately
  };

  // Handle word translation after confirmation
  const handleWordTranslationConfirmed = () => {
    if (pendingWordTranslation) {
      setTranslationCounts(prev => ({ ...prev, words: prev.words + 1 }));
      markFirstWordTranslationUsed();
      // Return the word so ClickableText can show the popover
      return pendingWordTranslation;
    }
    return null;
  };

  const handleTranslateIntro = async () => {
    if (!currentCase || introTranslated) return;
    
    // Check if first translation of the day
    if (isFirstTranslationOfDay()) {
      setTranslationType('intro');
      setPendingTranslationAction(() => async () => {
        try {
          const translation = await translateText(currentCase.intro, 'en', 'pt');
          setIntroTranslated(true);
          setIntroTranslationText(translation);
          setTranslationCounts(prev => ({ ...prev, intro: prev.intro + 1 }));
          markFirstTranslationUsed();
          return translation;
        } catch (error) {
          console.error('Translation error:', error);
          return null;
        }
      });
      setShowTranslationConfirm(true);
      return null;
    }

    // Not first translation, proceed normally
    try {
      const translation = await translateText(currentCase.intro, 'en', 'pt');
      setIntroTranslated(true);
      setIntroTranslationText(translation);
      setTranslationCounts(prev => ({ ...prev, intro: prev.intro + 1 }));
      return translation;
    } catch (error) {
      console.error('Translation error:', error);
      return null;
    }
  };

  const handleTranslateQuestion = async (questionId: string, questionText: string) => {
    if (questionTranslations.has(questionId)) {
      return questionTranslations.get(questionId) || null;
    }
    
    // Check if first translation of the day
    if (isFirstTranslationOfDay()) {
      setTranslationType('question');
      setPendingTranslationAction(() => async () => {
        try {
          const translation = await translateText(questionText, 'en', 'pt');
          setQuestionTranslations(prev => new Map(prev).set(questionId, translation));
          setTranslationCounts(prev => ({ ...prev, questions: prev.questions + 1 }));
          markFirstTranslationUsed();
          return translation;
        } catch (error) {
          console.error('Translation error:', error);
          return null;
        }
      });
      setShowTranslationConfirm(true);
      return null;
    }

    // Not first translation, proceed normally
    try {
      const translation = await translateText(questionText, 'en', 'pt');
      setQuestionTranslations(prev => new Map(prev).set(questionId, translation));
      setTranslationCounts(prev => ({ ...prev, questions: prev.questions + 1 }));
      return translation;
    } catch (error) {
      console.error('Translation error:', error);
      return null;
    }
  };

  const handleTranslateTip = async (tipId: string, tipText: string) => {
    if (tipTranslations.has(tipId)) {
      return tipTranslations.get(tipId) || null;
    }
    
    // Check if first translation of the day
    if (isFirstTranslationOfDay()) {
      setTranslationType('tip');
      setPendingTranslationAction(() => async () => {
        try {
          const translation = await translateText(tipText, 'en', 'pt');
          setTipTranslations(prev => new Map(prev).set(tipId, translation));
          setTranslationCounts(prev => ({ ...prev, tips: prev.tips + 1 }));
          markFirstTranslationUsed();
          return translation;
        } catch (error) {
          console.error('Translation error:', error);
          return null;
        }
      });
      setShowTranslationConfirm(true);
      return null;
    }

    // Not first translation, proceed normally
    try {
      const translation = await translateText(tipText, 'en', 'pt');
      setTipTranslations(prev => new Map(prev).set(tipId, translation));
      setTranslationCounts(prev => ({ ...prev, tips: prev.tips + 1 }));
      return translation;
    } catch (error) {
      console.error('Translation error:', error);
      return null;
    }
  };

  const handleRevealAnswer = () => {
    if (!currentCase) return;
    const currentQuestion = currentCase.questions[currentQuestionIndex];
    if (currentQuestion && !revealedAnswers.has(currentQuestion.id)) {
      // Show confirmation modal before revealing
      setPendingRevealAction(() => () => {
        setRevealedAnswers(prev => new Set(prev).add(currentQuestion.id));
      });
      setShowRevealConfirm(true);
    } else if (currentQuestion) {
      // Already revealed, just add it
      setRevealedAnswers(prev => new Set(prev).add(currentQuestion.id));
    }
  };

  const handleNextQuestion = () => {
    if (!currentCase) return;
    
    // Find the first unanswered question (don't increment index if current question wasn't answered)
    const firstUnansweredIndex = currentCase.questions.findIndex(q => !answeredQuestions.has(q.id));
    
    if (firstUnansweredIndex >= 0) {
      setCurrentQuestionIndex(firstUnansweredIndex);
      setIsQuestionModalOpen(true);
    }
  };

  // Handle tip narration
  const handleNarrateTip = (text: string, tipId: string, voice?: string) => {
    if (isSpeaking && playingId === tipId) {
      stop();
      setPlayingId(null);
    } else {
      stop(); // Stop any current speech
      const lang = voice ? voice.split('-').slice(0, 2).join('-') : 'en-US';
      speak(text, lang);
      setPlayingId(tipId);
    }
  };

  const handleGuessConfirm = async () => {
    if (!selectedSuspect || !currentCase || !currentUser || !dateKey) return;
    setIsGuessModalOpen(false);
    stop(); // stop speech

    const isWin = selectedSuspect.id === currentCase.solution.culpritSuspectId;
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);

    // Calculate Score according to spec
    const scoring = currentCase.scoring;
    let score = 0;
    const breakdown = [];

    if (isWin) {
      // Base points for correct answer
      score += scoring.b;
      breakdown.push({ label: 'Correct Answer', value: scoring.b });
    } else {
      // No base points for wrong answer
      breakdown.push({ label: 'Wrong Suspect', value: 0 });
    }

    // Bonus for correct unlock questions
    const questionBonus = correctQuestions * scoring.bq;
    if (questionBonus > 0) {
      score += questionBonus;
      breakdown.push({ label: `Correct Questions (${correctQuestions})`, value: questionBonus });
    }

    // Penalties
    const penalizedTipsCount = Math.max(0, revealedTipsCount - correctQuestions);
    const tipsPenalty = penalizedTipsCount * scoring.pt;
    if (tipsPenalty > 0) {
      score -= tipsPenalty;
      breakdown.push({ label: `Tips Used (${penalizedTipsCount})`, value: -tipsPenalty });
    }

    // Question penalty (-10 per question answered)
    const answeredCount = answeredQuestions.size;
    if (answeredCount > 0) {
      score -= answeredCount * 10;
      breakdown.push({ label: `Questions Answered (${answeredCount})`, value: -(answeredCount * 10) });
    }

    // Translation penalties
    const introTranslationPenalty = translationCounts.intro * scoring.trI;
    if (introTranslationPenalty > 0) {
      score -= introTranslationPenalty;
      breakdown.push({ label: `Intro Translation (${translationCounts.intro})`, value: -introTranslationPenalty });
    }

    const questionTranslationPenalty = translationCounts.questions * scoring.trT;
    if (questionTranslationPenalty > 0) {
      score -= questionTranslationPenalty;
      breakdown.push({ label: `Question Translations (${translationCounts.questions})`, value: -questionTranslationPenalty });
    }

    const tipTranslationPenalty = translationCounts.tips * scoring.trT;
    if (tipTranslationPenalty > 0) {
      score -= tipTranslationPenalty;
      breakdown.push({ label: `Tip Translations (${translationCounts.tips})`, value: -tipTranslationPenalty });
    }

    const wordTranslationPenalty = translationCounts.words * scoring.trW;
    if (wordTranslationPenalty > 0) {
      score -= wordTranslationPenalty;
      breakdown.push({ label: `Word Translations (${translationCounts.words})`, value: -wordTranslationPenalty });
    }

    // Reveal answer penalty (use tip penalty as default, or could be separate)
    const revealPenalty = revealedAnswers.size * scoring.pt; // Using tip penalty for reveals
    if (revealPenalty > 0) {
      score -= revealPenalty;
      breakdown.push({ label: `Answers Revealed (${revealedAnswers.size})`, value: -revealPenalty });
    }

    // Removed floor at 0: score can be negative if wrong accusation

    setLastResult({
      isWin,
      score,
      breakdown
    });

    setHasPlayed(true);
    setIsSolved(isWin);
    setGameState('result');
    
    // Clean up saved state
    const storageKey = `crime-game-progress-${currentUser.uid}-${dateKey}-${currentCase.id}`;
    localStorage.removeItem(storageKey);

    // Submit to Firebase
    try {
      await submitAttemptAndUpdateLeaderboard({
        dateKey,
        uid: currentUser.uid,
        displayName: currentUser.displayName || 'Detective',
        photoURL: currentUser.photoURL || undefined,
        result: isWin ? 'win' : 'loss',
        guessedSuspectId: selectedSuspect.id,
        timeSpentSec: timeSpent,
        hintsUsed: revealedTipsCount, // Using tips as hints
        deltaPoints: score
      });
      
      // Refresh user stats after successful submission
      try {
        const updatedStats = await fetchCurrentUserLeaderboardEntry(currentUser.uid, 'global');
        const newStreak = updatedStats?.dailyStreak || 0;
        
        // Store previous streak before updating
        if (userStats) {
          setPreviousStreak(userStats.dailyStreak || 0);
        }
        
        setUserStats(updatedStats);
        
        // Fetch week history
        const weekHistory = await fetchCrimeWeekActivity(currentUser.uid);
        setCrimeWeekHistory(weekHistory);
        
        // Show summary immediately
        setShowSummary(true);
      } catch (e) {
        console.error("Failed to refresh user stats", e);
      }
      
      // Dispatch event to notify navbar and other components about streak update
      console.log('CrimeGameContainer - Dispatching crimeGameStreakUpdated event');
      window.dispatchEvent(new CustomEvent('crimeGameStreakUpdated'));
    } catch (e) {
      console.error("Failed to submit result", e);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
        <p className="text-gray-500">Loading Crime History...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Unavailable</h2>
        <p className="text-gray-600">{error}</p>
        <button onClick={initGame} className="mt-4 text-indigo-600 hover:underline">Retry</button>
      </div>
    );
  }

  // Case Selection Screen (if multiple cases)
  // Landing Page: Crime of the Day
  if (gameState === 'landing' && currentCase) {
    return (
      <div className="max-w-4xl mx-auto px-2 md:px-4 py-6 pb-24 md:pb-6 min-h-screen flex flex-col">
        {/* Beautiful Header similar to Day Word */}
        <div className="text-center mb-12 mt-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="bg-indigo-100 p-3 rounded-full">
                <Siren className="w-10 h-10 text-indigo-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900">Crime of the Day</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Put your detective hat on! A new mystery awaits you every day.
            </p>
            <p className="text-md text-gray-500 mt-2 font-mono">
               {originalDateKey || dateKey}
            </p>

            {/* Streak Display */}
            {currentUser && (
              <div className="flex justify-center mt-6">
                {(userStats?.dailyStreak || 0) > 0 ? (
                  <div className="bg-orange-50 border-2 border-orange-200 rounded-full px-6 py-2 flex items-center gap-2 shadow-sm animate-pulse">
                    <Flame className="w-5 h-5 text-orange-500 fill-orange-500" />
                    <span className="font-bold text-gray-800">
                      {userStats?.dailyStreak} Day Streak
                    </span>
                  </div>
                ) : (
                  <div className="bg-gray-100 border-2 border-gray-300 rounded-full px-6 py-2 flex items-center gap-2 shadow-sm">
                    <Frown className="w-5 h-5 text-gray-500" />
                    <span className="font-bold text-gray-600">
                      0 Day Streak
                    </span>
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Rules Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-indigo-100 overflow-hidden mb-6">
          <div className="bg-indigo-50 px-6 py-4 border-b border-indigo-100">
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-bold text-gray-900">Game Rules</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4 text-sm text-gray-700">
              <div className="flex items-start gap-3">
                <span className="text-red-600 font-bold text-lg">⚠️</span>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Translations Cost Points</p>
                  <p>Using translation features will reduce your score:
                    <span className="font-mono ml-1 text-red-600">-{currentCase.scoring.trI}pts</span> for intro,
                    <span className="font-mono ml-1 text-red-600">-{currentCase.scoring.trT}pts</span> for questions/tips,
                    <span className="font-mono ml-1 text-red-600">-{currentCase.scoring.trW}pts</span> for words
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-600 font-bold text-lg">👁️</span>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Revealing Answers or Answering Incorrectly Costs Points</p>
                  <p>Each time you reveal an answer or answer incorrectly, you lose <span className="font-mono text-red-600">-{currentCase.scoring.pt}pts</span></p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-blue-600 font-bold text-lg">✓</span>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">You Don't Need All Answers</p>
                  <p>You can make an accusation at any time! Answering all questions is optional - you can skip questions and still solve the case.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-green-600 font-bold text-lg">+</span>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Earn Bonus Points</p>
                  <p>Answer questions correctly to earn <span className="font-mono text-green-600">+{currentCase.scoring.bq}pts</span> per correct answer</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Case Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-indigo-100 overflow-hidden mb-12 transform hover:scale-[1.01] transition-transform duration-300">
           <div className="bg-indigo-600 px-8 py-4 flex justify-between items-center text-white">
              <span className="font-bold tracking-wider text-sm uppercase">Daily Mystery</span>
              <Search className="w-5 h-5 opacity-75" />
           </div>

           <div className="p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">{currentCase.title}</h2>
              <div className="text-xl text-gray-600 leading-relaxed mb-10 max-w-3xl mx-auto text-left">
                <p>
                  {currentCase.intro.length > 200 
                    ? currentCase.intro.substring(0, 200).trim() + '...'
                    : currentCase.intro}
                </p>
              </div>

              <button
                onClick={() => !hasPlayed && selectCase(0)}
                disabled={hasPlayed}
                className={`inline-flex items-center px-8 py-4 text-white text-lg font-bold rounded-full shadow-lg transition-all ${
                  hasPlayed
                    ? (isSolved ? 'bg-emerald-600 opacity-90 cursor-not-allowed' : 'bg-gray-500 opacity-90 cursor-not-allowed')
                    : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200 transform hover:-translate-y-1'
                }`}
              >
                {hasPlayed ? (
                  <div className="flex flex-col items-center">
                    <div className="flex items-center">
                      {isSolved ? 'Case Solved!' : 'Case Closed!'}
                      {isSolved ? <Trophy className="ml-2 w-5 h-5" /> : <BookOpen className="ml-2 w-5 h-5" />}
                    </div>
                    <span className="text-sm font-normal mt-1 opacity-90">Come back tomorrow for a new case</span>
                  </div>
                ) : (
                  <>
                    Continue reading to solve the crime game
                    <Search className="ml-2 w-5 h-5" />
                  </>
                )}
              </button>
           </div>
        </div>

        {/* Secondary: View All / History */}
        <div className="text-center">
           <p className="text-gray-500 mb-4">Want to solve more cases?</p>
           <button
             onClick={() => setGameState('selection')}
             className="inline-flex items-center px-6 py-2 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
           >
             <BookOpen className="mr-2 w-4 h-4" />
             View Past Cases
           </button>
        </div>

        <LeaderboardPanel dateKey={originalDateKey || dateKey || undefined} />
      </div>
    );
  }

  const handleSelectPastDate = (date: string) => {
    setSelectedPastDate(date);
    const dateCases = casesByDate.get(date) || [];
    setCases(dateCases);
    setDateKey(date);
  };

  const handleSelectPastCase = (date: string, caseIndex: number) => {
    const dateCases = casesByDate.get(date) || [];
    setCases(dateCases);
    setDateKey(date);
    selectCase(caseIndex);
  };

  // Selection Screen (Secondary Place)
  if (gameState === 'selection') {
    return (
      <div className="max-w-4xl mx-auto px-2 md:px-4 py-4 pb-24 md:pb-4">
        <div className="flex items-center justify-between mb-8 mt-4">
           <h1 className="text-3xl font-bold text-gray-900">Case Files</h1>
           <button
             onClick={() => {
               setGameState('landing');
               setSelectedPastDate(null);
               // Reset to original date's cases
               if (originalDateKey && originalCases.length > 0) {
                 setDateKey(originalDateKey);
                 setCases(originalCases);
               }
             }}
             className="text-indigo-600 font-medium hover:underline"
           >
             &larr; Back to Crime of the Day
           </button>
        </div>

        {loadingPastCases ? (
          <div className="flex flex-col items-center justify-center min-h-[40vh]">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
            <p className="text-gray-500">Loading past cases...</p>
          </div>
        ) : selectedPastDate ? (
          // Show cases for selected date
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-500">Cases from: <span className="font-semibold text-gray-900">{selectedPastDate}</span></p>
              <button
                onClick={() => setSelectedPastDate(null)}
                className="text-indigo-600 font-medium hover:underline text-sm"
              >
                &larr; Back to Date Selection
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {cases.map((c, idx) => (
                 <div
                   key={c.id}
                   onClick={() => handleSelectPastCase(selectedPastDate, idx)}
                   className="bg-white p-6 rounded-xl shadow-md cursor-pointer hover:shadow-xl hover:scale-105 transition-all border border-gray-100"
                 >
                   <h2 className="text-xl font-bold mb-2">{c.title}</h2>
                   <p className="text-gray-600 line-clamp-3">{c.intro}</p>
                   <div className="mt-4 text-indigo-600 font-medium flex items-center">
                     Solve Case <Search className="w-4 h-4 ml-1" />
                   </div>
                 </div>
              ))}
            </div>
            <LeaderboardPanel dateKey={selectedPastDate} />
          </>
        ) : (
          // Show date selection
          <>
            <p className="text-gray-500 mb-6">Select a date to view cases from past days:</p>
            
            {pastDates.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No past cases available.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pastDates.slice(0, visiblePastDates).map((date) => {
                  const dateCases = casesByDate.get(date) || [];
                  return (
                    <div
                      key={date}
                      className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all cursor-pointer"
                      onClick={() => handleSelectPastDate(date)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-1">
                            {date}
                            {solvedDates.has(date) ? (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Solved</span>
                            ) : playedDates.has(date) ? (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">Played</span>
                            ) : null}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {dateCases.length} {dateCases.length === 1 ? 'case' : 'cases'} available
                          </p>
                        </div>
                        <div className="flex items-center text-indigo-600">
                          <BookOpen className="w-5 h-5 mr-2" />
                          <span className="font-medium">View Cases</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {visiblePastDates < pastDates.length && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                      className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {isLoadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load More Cases'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-2 md:px-4 py-4 pb-24 md:pb-4 min-h-screen flex flex-col">
      {/* Header */}
      <header className="flex justify-between items-center mb-6 py-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crime History</h1>
          <p className="text-sm text-gray-500">{dateKey} • {currentCase?.title}</p>
        </div>
        <div className="flex space-x-2">
           <button
             onClick={() => {
               setSearchParams({});
               setGameState('landing');
             }} // Go back to landing instead of selection
             className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
           >
             Exit
           </button>
        </div>
      </header>

      {/* Intro Step Removed */}

      {/* Game Loop */}
      {gameState === 'investigation' && currentCase && (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 order-2 lg:order-1">
             {/* Crime History Actions Toolbar */}
             <div className="flex justify-end items-center gap-2 mb-2 max-w-3xl mx-auto">
               {isSupported && (
                 <button
                   onClick={() => {
                     if (isSpeaking && playingId === 'intro') {
                       stop();
                       setPlayingId(null);
                     } else {
                       stop();
                       speak(currentCase.intro);
                       setPlayingId('intro');
                     }
                   }}
                   className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors border shadow-sm ${
                     isSpeaking && playingId === 'intro'
                       ? 'border-red-500 text-red-600 bg-red-50 hover:bg-red-100' 
                       : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
                   }`}
                   title={isSpeaking && playingId === 'intro' ? "Stop Narration" : "Narrate Crime History"}
                 >
                   {isSpeaking && playingId === 'intro' ? (
                     <>
                       <VolumeX className="w-4 h-4" />
                       <span>Stop</span>
                     </>
                   ) : (
                     <>
                       <Volume2 className="w-4 h-4" />
                       <span>Listen to History</span>
                     </>
                   )}
                 </button>
               )}
               {!introTranslated && (
                 <button
                   onClick={handleTranslateIntro}
                   className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 text-gray-600 rounded-lg transition-colors border border-gray-200 shadow-sm"
                   title={`Translate to Portuguese (-${currentCase.scoring.trI} points)`}
                 >
                   <Languages className="w-4 h-4" />
                   <span>Translate</span>
                 </button>
               )}
             </div>

             {/* Crime History Card */}
             <div className="bg-white rounded-lg shadow-md p-6 mb-6 max-w-3xl mx-auto">
               <h2 className="text-2xl font-bold text-gray-800 mb-4">{currentCase.title}</h2>
               <div className="prose prose-lg text-gray-600 max-w-none text-left leading-relaxed">
                 <div>
                   {formatIntoParagraphs(introTranslationText || currentCase.intro).map((paragraph, index) => (
                     <p key={index} className="mb-4 last:mb-0">
                       <ClickableText text={paragraph} onWordClick={handleWordClick} pendingWord={confirmedWordTranslation} />
                     </p>
                   ))}
                 </div>
                 {introTranslated && introTranslationText && (
                   <div className="text-sm text-gray-500 italic mt-6 border-t pt-4">
                     {formatIntoParagraphs(currentCase.intro).map((paragraph, index) => (
                       <p key={index} className="mb-4 last:mb-0">
                         <ClickableText text={paragraph} onWordClick={handleWordClick} pendingWord={confirmedWordTranslation} />
                       </p>
                     ))}
                   </div>
                 )}
               </div>
             </div>

             <div className="mb-4 flex items-center justify-end px-2 md:px-4 max-w-3xl mx-auto">
               {isSupported && (
                 <label className="flex items-center space-x-2 cursor-pointer text-sm text-gray-600">
                   <input
                     type="checkbox"
                     checked={autoSpeak}
                     onChange={(e) => setAutoSpeak(e.target.checked)}
                     className="form-checkbox h-4 w-4 text-indigo-600 rounded"
                   />
                   <span>Auto-narrate clues</span>
                 </label>
               )}
             </div>

             {/* Important Notice / Start Here */}
             <div className="max-w-3xl mx-auto mb-6">
               <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm">
                 <div className="flex items-start gap-3">
                   <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                   <div className="text-sm text-gray-700">
                     <p className="font-semibold text-gray-900 mb-1 text-base">Start by answering questions to unlock clues!</p>
                     <p className="mb-2">
                       Click the <strong className="text-indigo-700">Answer Next Question</strong> button below to gather evidence. Each tip you unlock costs <span className="font-mono font-semibold text-red-600">-{currentCase.scoring.pt}pts</span>, but answering correctly gives you bonus points!
                     </p>
                     <p className="text-xs text-gray-600 italic">
                       You can make an accusation at any time, but it's highly recommended to gather enough evidence first.
                     </p>
                   </div>
                 </div>
               </div>
             </div>

             <CluePlayer
               tips={unlockedTips}
               revealedCount={unlockedTips.length}
               onNarrateTip={handleNarrateTip}
               onTranslateTip={handleTranslateTip}
               onWordClick={handleWordClick}
               isSpeaking={isSpeaking}
               playingId={playingId}
               isSpeechSupported={isSupported}
               suspectNames={suspectNameMap}
               translationPenalty={currentCase.scoring.trT}
               tipTranslations={tipTranslations}
             />

             <AnsweredQuestionsLog 
               questions={currentCase.questions} 
               answeredQuestions={answeredQuestions}
               correctlyAnsweredQuestions={correctlyAnsweredQuestions}
               wronglyAnsweredQuestions={wronglyAnsweredQuestions}
               revealedAnswers={revealedAnswers}
               skippedQuestions={skippedQuestions}
               onWordClick={handleWordClick}
               pendingWord={confirmedWordTranslation}
             />

             {/* Question Progress */}
             <div className="max-w-3xl mx-auto mt-4 mb-4">
               <div className="text-center text-sm text-gray-600">
                 Questions answered: {answeredQuestions.size} / {currentCase.questions.length}
                 
                 <div className="flex justify-center gap-4 mt-4">
                   {(() => {
                     const canAnswerQuestion = answeredQuestions.size < currentCase.questions.length;
                     const canRevealTip = revealedNextTipFor.size < currentCase.tips.length;
                     
                     if (!canAnswerQuestion && !canRevealTip) return null;
                     
                     return (
                       <>
                         {canAnswerQuestion && (
                           <button
                             onClick={handleNextQuestion}
                             className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 shadow-sm transition-colors"
                           >
                             Answer Next Question
                           </button>
                         )}
                         {canRevealTip && (
                           <button
                             onClick={handleRevealNextTip}
                             className="px-4 py-2 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 shadow-sm transition-colors"
                           >
                             Reveal Next Tip (-{currentCase.scoring.pt}pts)
                           </button>
                         )}
                       </>
                     );
                   })()}
                 </div>
               </div>
             </div>

             {/* Action Bar */}
             <div className="flex justify-center mt-8 mb-12">
                <button
                  onClick={() => setIsGuessModalOpen(true)}
                  className={`px-8 py-4 text-lg font-bold rounded-full shadow-lg transition-transform ${
                    selectedSuspect
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-105 animate-pulse cursor-pointer'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 hover:scale-105 cursor-pointer'
                  }`}
                >
                  Make an Accusation
                </button>
             </div>
          </div>

          <div className="w-full lg:w-1/3 order-1 lg:order-2">
             <h3 className="text-lg font-bold mb-4 text-center">Suspects</h3>
             <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
               <SuspectGrid
                 suspects={actualSuspects}
                 onSelect={setSelectedSuspect}
                 selectedId={selectedSuspect?.id}
               layout="sidebar"
               />
             </div>
          </div>
        </div>
      )}

      {/* Question Modal */}
      {isQuestionModalOpen && currentCase && (
        <QuestionModal
          isOpen={isQuestionModalOpen}
          question={currentCase.questions[currentQuestionIndex] || null}
          onAnswer={handleQuestionAnswer}
          onClose={() => setIsQuestionModalOpen(false)}
          onSkip={handleSkipQuestion}
          onTranslate={(text) => {
            const question = currentCase.questions[currentQuestionIndex];
            if (question) {
              return handleTranslateQuestion(question.id, text);
            }
            return Promise.resolve(null);
          }}
          onRevealAnswer={handleRevealAnswer}
          translationPenalty={currentCase.scoring.trT}
          revealPenalty={currentCase.scoring.pt}
          questionTranslated={currentCase.questions[currentQuestionIndex] ? 
            questionTranslations.has(currentCase.questions[currentQuestionIndex].id) : false}
          answerRevealed={currentCase.questions[currentQuestionIndex] ? 
            revealedAnswers.has(currentCase.questions[currentQuestionIndex].id) : false}
          canRevealNextTip={
            currentCase.questions[currentQuestionIndex] 
            ? !revealedNextTipFor.has(currentCase.questions[currentQuestionIndex].id) 
            : false
          }
          onRevealNextTip={handleRevealNextTip}
        />
      )}

      {/* Result Screen */}
      {gameState === 'result' && lastResult && currentCase && (
        <>
          <ResultPanel
            isWin={lastResult.isWin}
            score={lastResult.score}
            pointsBreakdown={lastResult.breakdown}
            solution={currentCase.solution}
            culprit={currentCase.suspects.find(s => s.id === currentCase.solution.culpritSuspectId)}
            reveal={currentCase.reveal}
            onPlayAgain={() => {
              setShowSummary(false);
              setGameState('selection');
            }}
            onTranslate={async (text: string) => {
              try {
                const translation = await translateText(text, 'en', 'pt');
                setTranslationCounts(prev => ({ ...prev, tips: prev.tips + 1 }));
                return translation;
              } catch (error) {
                console.error('Translation error:', error);
                return null;
              }
            }}
            translationPenalty={currentCase.scoring.trT}
            onGoToDashboard={() => navigate('/dashboard')}
          />
          <LeaderboardPanel dateKey={dateKey || undefined} />
        </>
      )}

      {/* Summary Screen - shown below ResultPanel */}
      {showSummary && gameState === 'result' && lastResult && userStats && (
        <FeatureSummary
          title={lastResult.isWin ? 'Case Solved!' : 'Case Complete!'}
          message={lastResult.isWin ? "Great detective work!" : "Keep investigating!"}
          score={lastResult.score}
          currentStreak={userStats.dailyStreak || 0}
          previousStreak={previousStreak}
          weekHistory={crimeWeekHistory}
          onContinue={() => {
            setShowSummary(false);
            setGameState('selection');
          }}
          continueLabel="Back to Cases"
          onSecondaryAction={() => navigate('/dashboard')}
          secondaryLabel="Back to Dashboard"
          colorScheme="orange"
        />
      )}

      <GuessModal
        isOpen={isGuessModalOpen}
        onClose={() => setIsGuessModalOpen(false)}
        onConfirm={handleGuessConfirm}
        selectedSuspect={selectedSuspect}
        suspects={actualSuspects}
        onSelectSuspect={setSelectedSuspect}
        answeredCount={answeredQuestions.size}
        totalQuestions={currentCase?.questions.length || 0}
        revealedCount={revealedTipsCount}
      />

      {/* Translation Confirmation Modal */}
      {currentCase && (
        <ConfirmationModal
          isOpen={showTranslationConfirm}
          onConfirm={() => {
            if (pendingTranslationAction) {
              pendingTranslationAction();
            }
            setShowTranslationConfirm(false);
            setPendingTranslationAction(null);
            setTranslationType(null);
          }}
          onCancel={() => {
            setShowTranslationConfirm(false);
            setPendingTranslationAction(null);
            setTranslationType(null);
          }}
          title="Translation Will Cost Points"
          message="Using translation will reduce your score. Are you sure you want to translate this text?"
          confirmText="Yes, Translate"
          cancelText="Cancel"
          penalty={
            translationType === 'intro' ? currentCase.scoring.trI :
            translationType === 'question' ? currentCase.scoring.trT :
            translationType === 'tip' ? currentCase.scoring.trT :
            currentCase.scoring.trT
          }
          type="translation"
        />
      )}

      {/* Reveal Answer Confirmation Modal */}
      {currentCase && (
        <ConfirmationModal
          isOpen={showRevealConfirm}
          onConfirm={() => {
            if (pendingRevealAction) {
              pendingRevealAction();
            }
            setShowRevealConfirm(false);
            setPendingRevealAction(null);
          }}
          onCancel={() => {
            setShowRevealConfirm(false);
            setPendingRevealAction(null);
          }}
          title="Reveal Answer Will Cost Points"
          message="Revealing the answer will reduce your score. Remember: you don't need to answer all questions to make an accusation!"
          confirmText="Yes, Reveal Answer"
          cancelText="Cancel"
          penalty={currentCase.scoring.pt}
          type="reveal"
        />
      )}

      {/* Word Translation Confirmation Modal */}
      {currentCase && (
        <ConfirmationModal
          isOpen={showWordTranslationConfirm}
          onConfirm={() => {
            handleWordTranslationConfirmed();
            setShowWordTranslationConfirm(false);
            setConfirmedWordTranslation(pendingWordTranslation);
            setTranslationType(null);
            setTimeout(() => {
              setPendingWordTranslation(null);
              setConfirmedWordTranslation(null);
            }, 100);
          }}
          onCancel={() => {
            setShowWordTranslationConfirm(false);
            setPendingWordTranslation(null);
            setTranslationType(null);
          }}
          title="Word Translation Will Cost Points"
          message={`Translating the word "${pendingWordTranslation}" will reduce your score by ${currentCase.scoring.trW} points.`}
          confirmText="Yes, Translate Word"
          cancelText="Cancel"
          penalty={currentCase.scoring.trW}
          type="translation"
        />
      )}

      {/* Skip Question Confirmation Modal */}
      {currentCase && (
        <ConfirmationModal
          isOpen={showSkipConfirm}
          onConfirm={() => {
            if (pendingSkipAction) {
              pendingSkipAction();
            }
            setShowSkipConfirm(false);
            setPendingSkipAction(null);
          }}
          onCancel={() => {
            setShowSkipConfirm(false);
            setPendingSkipAction(null);
          }}
          title="Skip Question"
          message="When you skip a question, the next question's tip will also be skipped (not shown). You won't lose any points for skipping, but you'll miss the opportunity to unlock that tip. You can still answer the next question if you want. Are you sure you want to skip?"
          confirmText="Yes, Skip Question"
          cancelText="Cancel"
          type="skip"
        />
      )}
    </div>
  );
}
